import { AdgroupConfig, AdgroupFormData, AppRetargetingType, CampaignFormData, PangleFlowType } from '@tt4b/ttam-enums';
import { StateEnum, useStore, useStdAdgroupIs, useAllowlist } from '@tt4b/ttam-hooks';
import { computed, Ref, ref } from '@vue/composition-api';
import { useGenderName } from '../gender/hooks';
import { i18n } from './../../../i18n';
import { TranslateResult } from 'vue-i18n';
import { useAgeName } from '../age/hooks';
import { usePropLanguageListName } from '../languages/hooks';
import { DPA_RETARGET_OPTIONS } from '../audience/consts';

import { usePlatformName } from '../operating-system/hooks';
import { useOperatorName } from '../carriers/hooks';
import { useOsvName } from '../os-version/hooks';
import { usePropsLocationName } from '../location/hooks';
import { useNetworkName } from '../network/hooks';
import { usePropDevicePrice } from '../device-price/hooks';
import { usePropDeviceModelName } from '../device-model/hooks';
import { useISPName } from '../isp/hooks';
import { useInterestBehaviorSourceData } from '../interest-behavior-five-in-one/hooks';
import { useTargetTypePropsVisible } from '../interests/hooks/target-type';
export type LabelItem = {
  label: string | TranslateResult;
  data?: string | string[] | number | TranslateResult;
  children?: LabelItem[];
  hideWhenNoLimit?: boolean;
};

export type LabelResult = {
  noLimitData: LabelItem[];
  showData: LabelItem[];
};

const noLimitName = () => i18n.t('adgroup_no_limit') as string;
const isGroupNoLimit = (data: LabelItem['data'] | undefined) => {
  if (Array.isArray(data)) {
    if (!data.length) {
      return true;
    }
    if (data.includes(noLimitName())) {
      return true;
    }
  }

  if (typeof data === 'string') {
    if (data === noLimitName() || data === '') {
      return true;
    }
  }

  return false;
};

// export function usePangleData(adgroupData: Ref<AdgroupFormData>) {
//   const {
//     _pangleFlowStatus,
//     _pangleFlowType,
//     pangleFlowPackageExcludeName,
//     pangleFlowPackageIncludeName,
//   } = usePangleFlow(adgroupData);

//   const pangleData = computed(() => {
//     const data: LabelItem[] = [];

//     if (!_pangleFlowStatus.value) {
//       return data;
//     }

//     if (_pangleFlowType.value === PangleFlowType.ORIENT) {
//       data.push({
//         label: i18n.t('common_flow_origent_package'),
//         data: pangleFlowPackageIncludeName.value,
//       });
//     }
//     if (_pangleFlowType.value === PangleFlowType.EXCLUDE) {
//       data.push({
//         label: i18n.t('common_flow_exclude_package'),
//         data: pangleFlowPackageExcludeName.value,
//       });
//     }

//     return data;
//   });

//   return {
//     pangleData,
//   };
// }

export function useAudience(options: { campaignData: Ref<CampaignFormData>; adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData, campaignData } = options;
  const { isStandardAdsWithCatalog, isProductRetargeting } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { state } = useStore();
  const dmpMap = computed(() =>
    state[StateEnum.dmpList].data.value?.reduce((result, item) => {
      result[item.id] = item.name;

      return result;
    }, {}),
  );

  const retargetMap = DPA_RETARGET_OPTIONS.reduce((acc, cur) => ({ ...acc, [cur.value]: i18n.t(cur.label) }), {});
  const retargetingTagsName = computed(() =>
    isProductRetargeting.value
      ? adgroupData.value?.includeCustomActions?.map?.(o => retargetMap[o.code]) || []
      : adgroupData.value?.retargetingTags?.map(item => dmpMap.value?.[item]),
  );
  const retargetingTagsExcludeName = computed(() =>
    isProductRetargeting.value
      ? adgroupData.value?.excludeCustomActions?.map?.(o => retargetMap[o.code]) || []
      : adgroupData.value?.retargetingTagsExclude?.map(item => dmpMap.value?.[item]),
  );

  const retargeting = computed(() => {
    const note =
      adgroupData.value.appRetargetingType === AppRetargetingType.INCLUDE_SPECIFIC_EVENTS
        ? i18n.t('module_app_retarget_include_interacte')
        : '';

    let parentLabel: TranslateResult = '';

    if (isStandardAdsWithCatalog.value) {
      parentLabel = isProductRetargeting.value
        ? i18n.t('module_dpa_ad_retarget')
        : i18n.t('module_adgroup_find_prospective_customers');
    }

    return [
      {
        parentLabel,
        label: i18n.t('adgroup_audience'),
        note,
        data: retargetingTagsName.value,
      },
      {
        label: i18n.t('adgroup_audicence_dmp_excluded'),
        data: retargetingTagsExcludeName.value,
      },
    ];
  });

  return {
    retargeting,
  };
}

export function useDevice(options: { campaignData: Ref<CampaignFormData>; adgroupData: Ref<AdgroupFormData> }) {
  const { campaignData, adgroupData } = options;
  const { isISPTargetWhite } = useAllowlist();
  const platform = computed(() => adgroupData.value.platform);
  const network = computed(() => adgroupData.value.ac);
  const { platformName } = usePlatformName(platform);
  const { osvName } = useOsvName({
    campaignData,
    adgroupData,
  });
  const { operatorName } = useOperatorName({
    adgroupData,
  });
  const { networkName } = useNetworkName({
    network,
  });
  const { devicePrice } = usePropDevicePrice({
    adgroupData,
  });
  const { deviceModelName } = usePropDeviceModelName({
    adgroupData,
  });

  const { ispName } = useISPName(adgroupData.value.internetServiceProviders);

  const device = computed(() => [
    {
      label: i18n.t('adgroup_platform'),
      data: platformName.value,
    },
    {
      label: i18n.t('adgroup_target_device_model'),
      data: deviceModelName.value,
    },
    {
      label: i18n.t('adgroup_network'),
      data: networkName.value,
    },
    {
      label: i18n.t('adgroup_operator_carrier'),
      data: operatorName.value,
    },
    ...(isISPTargetWhite.value
      ? [
          {
            label: i18n.t('adgroup_isp_target'),
            data: ispName.value,
          },
        ]
      : []),
    {
      label: i18n.t('adgroup_device_price'),
      data: devicePrice.value,
    },
    {
      label: i18n.t('common_os_version'),
      data: osvName.value,
    },
  ]);

  return {
    device,
  };
}

export function useDemographicsData(adgroupData: Ref<AdgroupFormData>) {
  // Demographics
  const gender = computed(() => adgroupData.value.gender);
  const age = computed(() => adgroupData.value.age);
  const { genderName } = useGenderName({
    gender,
  });
  const { ageName } = useAgeName({
    age,
  });
  const { languageListName } = usePropLanguageListName({
    adgroupData,
  });
  const demographics = computed(() => [
    {
      label: i18n.t('adgroup_gender'),
      data: genderName.value,
    },
    {
      label: i18n.t('adgroup_age'),
      data: ageName.value,
    },
    {
      label: i18n.t('adgroup_delivery_language'),
      data: languageListName.value,
    },
  ]);

  return {
    demographics,
  };
}

export function useInterestData(options: { campaignData: Ref<CampaignFormData>; adgroupData: Ref<AdgroupFormData> }) {
  const { campaignData, adgroupData } = options;
  const {
    videoLabel: creatorBehaviorLabel,
    creatorLabel: videoBehaviorLabel,
    hashtagLabel: hashtagBehaviorLabel,
    adTagV2Name: interestName,
    inMarketLabel,
    interestKeywordsName,
  } = useInterestBehaviorSourceData({
    adgroupData,
  });
  const { isHasTikTok, isOnlyPangle } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { visible: isInMarketTargetAvailable } = useTargetTypePropsVisible({
    campaignData,
    adgroupData,
  });
  const isShowBehavior = computed(() => isHasTikTok.value);
  const isShowInterest = computed(() => !isOnlyPangle.value);
  const isShowInterestBehavior = computed(() => isShowInterest.value || isShowBehavior.value);
  const interestTags = computed(() => {
    const interestCategoriesName = Array.isArray(interestName.value) ? interestName.value : [];
    const tags = [...interestCategoriesName, ...interestKeywordsName.value];

    return tags.length ? tags : noLimitName();
  });

  const interestBehaviors = computed(() => {
    if (!isShowInterestBehavior.value) {
      return [];
    }

    const data: LabelItem = {
      // 只展示 adTagV2 和 actionCategoriesV2
      label: i18n.t('module_adgroup_interest_behavior'),
      children: [
        {
          label: i18n.t('data_report_dimemsions_interest_tag'),
          data: interestTags.value,
        },
        {
          label: i18n.t('module_adgroup_behavior_tab_label'),
          data: videoBehaviorLabel.value || noLimitName(),
        },
        {
          label: i18n.t('module_adgroup_behavior_tab_label'),
          data: creatorBehaviorLabel.value || noLimitName(),
        },
        {
          label: i18n.t('module_adgroup_behavior_tab_label'),
          data: hashtagBehaviorLabel.value || noLimitName(),
        },
      ],
    };
    if (isInMarketTargetAvailable.value) {
      data.children?.push({
        label: i18n.t('module_adgroup_target_interest_puechase_intention'),
        data: inMarketLabel.value || noLimitName,
      });
    }
    return [data];
  });

  return {
    interestBehaviors,
  };
}

export function usePropsTargetSummaryData(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { campaignData, adgroupData } = options;
  const targetSummaryData = computed(() => {
    // Audience and retargeting
    const { retargeting } = useAudience({
      campaignData,
      adgroupData,
    });

    const { selectedlocationsName } = usePropsLocationName({
      adgroupData,
    });
    const locations = computed(() => [
      {
        label: i18n.t('adgroup_district'),
        data: selectedlocationsName.value,
        hideWhenNoLimit: true,
      },
    ]);

    // Demographics
    const { demographics } = useDemographicsData(adgroupData);

    // Interest & Behavior
    const { interestBehaviors } = useInterestData({
      campaignData,
      adgroupData,
    });

    // Pangle flow
    // const { pangleData } = usePangleData(adgroupData);

    // Device
    const { device } = useDevice({
      campaignData,
      adgroupData,
    });

    const allOptions: LabelItem[] = [
      ...retargeting.value,
      ...locations.value,
      ...demographics.value,
      ...interestBehaviors.value,
      // ...pangleData.value,
      ...device.value,
    ];

    return allOptions.reduce(
      (result: LabelResult, item) => {
        if (item.children) {
          const isAllChildrenNolimit = item.children.every(child => isGroupNoLimit(child.data));
          if (isAllChildrenNolimit) {
            result.noLimitData.push(item);
          } else {
            result.showData.push(item);
          }

          return result;
        }
        if (isGroupNoLimit(item.data) && !item?.hideWhenNoLimit) {
          result.noLimitData.push(item);
        } else {
          result.showData.push(item);
        }

        return result;
      },
      {
        noLimitData: [],
        showData: [],
      },
    );
  });

  return {
    targetSummaryData,
  };
}

export function usePropsVisible(options: { adgroupData: Ref<AdgroupFormData>; adgroupConfig: Ref<AdgroupConfig> }) {
  const { adgroupData, adgroupConfig } = options;
  const visible = computed(
    () =>
      adgroupConfig.value.showTargetSummary &&
      (Boolean(adgroupData.value.particleLocations?.length) || Boolean(adgroupData.value.zipcodeIds?.length)),
  );

  return {
    visible,
  };
}
